/**
 * NumberInput — boilerplate UI primitive
 *
 * A number field with visible increment / decrement buttons.
 * The base <Input type="number"> hides native browser spinners, so this
 * component provides explicit +/- buttons for step-based editing.
 *
 * Props mirror the ones you'd pass to a normal <Input> for numbers.
 */

import React, { useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { t } from '@/lib/i18n';

export interface NumberInputProps {
  /** Current numeric value, or null for empty. */
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  /** Step used by increment/decrement buttons. */
  step?: number;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  hint?: string;
  error?: string;
  className?: string;
  /**
   * compact — uses smaller buttons and narrower input; suitable for
   * inline use inside a row (e.g. agenda item duration).
   */
  compact?: boolean;
  'data-testid'?: string;
}

export function NumberInput({
  value,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  disabled = false,
  placeholder,
  label,
  hint,
  error,
  className,
  compact = false,
  'data-testid': testId,
}: NumberInputProps): React.JSX.Element {
  const canDecrement = value === null ? false : value - step >= min;
  const canIncrement = value === null ? max !== Infinity : value + step <= max;

  const decrement = useCallback(() => {
    const current = value ?? min;
    const next = Math.max(min, current - step);
    onChange(next);
  }, [value, min, step, onChange]);

  const increment = useCallback(() => {
    const current = value ?? min;
    const next = Math.min(max, current + step);
    onChange(next);
  }, [value, min, max, step, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(null);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed)) {
      onChange(Math.min(max, Math.max(min, parsed)));
    }
  };

  const btnClass = compact
    ? 'h-7 w-7 shrink-0 p-0'
    : 'h-10 w-10 shrink-0 p-0';
  const inputClass = compact ? 'w-12 text-xs text-center px-1' : 'text-center';

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label}
        </label>
      )}

      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={btnClass}
          disabled={disabled || !canDecrement}
          onClick={decrement}
          aria-label={t('Decrease')}
          tabIndex={-1}
        >
          <Minus className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        </Button>

        <Input
          type="number"
          value={value ?? ''}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          min={min}
          max={max === Infinity ? undefined : max}
          step={step}
          className={inputClass}
          data-testid={testId}
        />

        <Button
          type="button"
          variant="outline"
          size="sm"
          className={btnClass}
          disabled={disabled || !canIncrement}
          onClick={increment}
          aria-label={t('Increase')}
          tabIndex={-1}
        >
          <Plus className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        </Button>
      </div>

      {hint && !error && (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      )}
      {error && (
        <p className="mt-1 text-sm text-error-600">{error}</p>
      )}
    </div>
  );
}
